import { useState, useEffect } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { formatDisplayDate } from '@/utils/dateFormat';

export function DataFreshnessBanner({ api }) {
  const [freshness, setFreshness] = useState(null);

  useEffect(() => {
    api.get('/upload/freshness').then(r => setFreshness(r.data)).catch(() => {});
  }, [api]);

  if (!freshness) return null;

  const of = freshness.order_fulfilment;
  if (!of) {
    return (
      <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-4 py-2.5 flex items-start gap-2.5" data-testid="freshness-banner">
        <AlertTriangle size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-red-700 dark:text-red-300">
          <strong>No Order Fulfilment data uploaded yet.</strong> Analytics and alerts require this data. Go to Configuration to upload.
        </p>
      </div>
    );
  }

  const uploadedAt = new Date(of.uploaded_at);
  const now = new Date();
  const hoursAgo = Math.floor((now - uploadedAt) / (1000 * 60 * 60));
  const daysAgo = Math.floor(hoursAgo / 24);

  if (hoursAgo < 24) return null; // Fresh data, no banner needed

  const isStale = daysAgo >= 7;
  const bgClass = isStale
    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
    : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900';
  const textClass = isStale ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300';
  const iconClass = isStale ? 'text-red-500' : 'text-amber-500';

  const timeLabel = daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
  const dateStr = formatDisplayDate(uploadedAt);
  const dataRange = of.data_from && of.data_to ? ` Data covers ${formatDisplayDate(of.data_from)} to ${formatDisplayDate(of.data_to)}.` : '';

  return (
    <div className={`border rounded-md px-4 py-2.5 flex items-start gap-2.5 ${bgClass}`} data-testid="freshness-banner">
      <Clock size={15} className={`${iconClass} mt-0.5 flex-shrink-0`} />
      <p className={`text-xs ${textClass}`}>
        <strong>Data freshness:</strong> Last Order Fulfilment upload was <strong>{timeLabel}</strong> ({dateStr} by {of.uploaded_by}).{dataRange} Alerts and analytics may not reflect the latest field activity.
      </p>
    </div>
  );
}
